/* eslint-disable */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { propertyStatus } from 'store/constant';
import axios from 'utils/axios';

const name = 'Listings';
const initialState = createInitialState();
const extraActions = createExtraActions();
const extraReducers = createExtraReducers();
const slice = createSlice({
    name,
    initialState,
    extraReducers
});

export const ListingActions = { ...slice.actions, ...extraActions };

export default slice.reducer;

function createInitialState() {
    return {
        loading: false,
        listings: [],
        complexDropdown: [],
        floorPlanDropdown: [],
        singleListing: {},
        totalData: 0,
        count: {},
        error: null,
        propertiesCount: {}
    };
}

function createExtraActions() {
    return {
        fetchAllListings: fetchAllListings(),
        fetchListingDetails: fetchListingDetails(),
        createListings: createListings(),
        createEmptyListings: createEmptyListings(),
        editListings: editListings(),
        duplicateListings: duplicateListings(),
        editListingStatus: editListingStatus(),
        deleteListings: deleteListings(),
        deleteRoomListings: deleteRoomListings(),
        deleteGreenEnergyBuildingListings: deleteGreenEnergyBuildingListings(),
        verifyListings: verifyListings(),
        shareListingAccess: shareListingAccess(),
        fetchSharedListings: fetchSharedListings(),
        createShotnerURL: createShotnerURL(),
        fetchShotnerURL: fetchShotnerURL(),
        addListingAnalytics: addListingAnalytics(),
        fetchListingsAccessess: fetchListingsAccessess(),
        updateArchieveListing: updateArchieveListing(),
        getCount: getCount(),
        syncListingWithAbor: syncListingWithAbor()
    };
}

function fetchAllListings() {
    return createAsyncThunk(
        `${name}/fetchAllListings`,
        async ({
            page,
            limit,
            search,
            status = propertyStatus.DEFAULT,
            managerId,
            filters,
            floorPlan,
            complex,
            sortByKey = null,
            sortByType = null,
            listingStatus
        }) => {
            try {
                const response = await axios.get(`/property`, {
                    params: {
                        page,
                        limit,
                        search,
                        status,
                        updatedFieldsPath: filters,
                        manager_id: managerId,
                        complex,
                        floorPlan,
                        sortByKey,
                        sortByType,
                        listingStatus
                    }
                });
                return response.data;
            } catch (err) {
                throw new Error(err?.errors?.message);
            }
        }
    );
}

function getCount() {
    return createAsyncThunk(`${name}/getCount`, async () => {
        try {
            const response = await axios.get(`/property/counts`);
            return response.data;
        } catch (err) {
            throw new Error(err?.errors?.message);
        }
    });
}
function fetchListingDetails() {
    return createAsyncThunk(`${name}/fetchListingDetails`, async (id) => {
        try {
            const response = await axios.get(`/property/${id}`);
            return response.data;
        } catch (err) {
            throw new Error(err?.errors?.message);
        }
    });
}

function createShotnerURL() {
    return createAsyncThunk(`${name}/createShotnerURL`, async (data) => {
        try {
            const response = await axios.post(`/urlShortner/shorten`, data);
            return response.data;
        } catch (err) {
            throw new Error(err?.errors?.message);
        }
    });
}

function addListingAnalytics() {
    return createAsyncThunk(`${name}/addListingAnalytics`, async (data) => {
        try {
            const response = await axios.put(`/property/upsertHitCounter/${data?.propertyId}`, data?.newFields);
            return response.data;
        } catch (err) {
            throw new Error(err?.error?.message);
        }
    });
}

function fetchShotnerURL() {
    return createAsyncThunk(`${name}/fetchShotnerURL`, async (id) => {
        try {
            const response = await axios.get(`/urlShortner/${id}`);

            return response.data;
        } catch (err) {
            throw new Error(err?.errors?.message);
        }
    });
}

function fetchSharedListings() {
    return createAsyncThunk(`${name}/fetchSharedListings`, async (id) => {
        try {
            const response = await axios.get(`/property/getSharedListings`);
            return response.data;
        } catch (err) {
            throw new Error(err?.errors?.message);
        }
    });
}

function fetchListingsAccessess() {
    return createAsyncThunk(`${name}/fetchListingsAccessess`, async (id) => {
        try {
            const response = await axios.get(`/property/accesses/${id}`);
            return response.data;
        } catch (err) {
            throw new Error(err?.errors?.message);
        }
    });
}

function createListings() {
    return createAsyncThunk(`${name}/createListings`, async (data) => {
        try {
            const response = await axios.post(`/property`, data);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function updateArchieveListing() {
    return createAsyncThunk(`${name}/updateArchieveListing`, async (data) => {
        try {
            const response = await axios.patch(`/property/verifyArchive`, data);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function createEmptyListings() {
    return createAsyncThunk(`${name}/createEmptyListings`, async (data) => {
        try {
            const response = await axios.post(`/property`, data);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function shareListingAccess() {
    return createAsyncThunk(`${name}/shareListingAccess`, async (data) => {
        try {
            const response = await axios.post(`/property/shareAccess`, data);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function getS3PresignedUrl() {
    return createAsyncThunk(`${name}/getS3PresignedUrl`, async (data) => {
        try {
            const response = await axios.get(`/attachments/upload/${data.key}`);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function editListings() {
    return createAsyncThunk(`${name}/editListings`, async (data) => {
        try {
            const response = await axios.patch(`/property/${data?.id}`, data?.values);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function duplicateListings() {
    return createAsyncThunk(`${name}/duplicateListings`, async (id) => {
        try {
            const response = await axios.post(`/property/duplicateAndActive/${id}`);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function editListingStatus() {
    return createAsyncThunk(`${name}/editListingStatus`, async (data) => {
        try {
            const response = await axios.patch(`/property/updateStatus/${data?.id}`, data?.values);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function verifyListings() {
    return createAsyncThunk(`${name}/verifyListings`, async (data) => {
        try {
            const response = await axios.patch(`/property/verify`, data?.values);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function deleteListings() {
    return createAsyncThunk(`${name}/deleteListings`, async (data) => {
        try {
            const response = await axios.delete(`/property`, {
                data: data
            });
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function deleteRoomListings() {
    return createAsyncThunk(`${name}/deleteRoomListings`, async (id) => {
        try {
            const response = await axios.delete(`/property/room/${id}`);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function deleteGreenEnergyBuildingListings() {
    return createAsyncThunk(`${name}/deleteGreenEnergyBuildingListings`, async (id) => {
        try {
            const response = await axios.delete(`/property/green_energy_building_verification/${id}`);
            return response;
        } catch (err) {
            throw err;
        }
    });
}

function syncListingWithAbor() {
    return createAsyncThunk(`${name}/syncListingWithAbor`, async (id) => {
        try {
            const response = await axios.post(`/property/syncDataWithMLS`,{
                listingId: id
            });
            return response.data;
        } catch (err) {
            throw err;
        }
    });
}

function createExtraReducers() {
    return {
        ...fetchAllListingsReducer(),
        ...editListingsReducer(),
        ...deleteListingsReducer(),
        ...fetchListingDetails(),
        ...fetchSharedListingsReducer(),
        ...getCount()
    };

    function getCount() {
        const { fulfilled } = extraActions.getCount;
        return {
            [fulfilled]: (state, action) => {
                state.propertiesCount = action.payload;
            }
        };
    }

    function fetchAllListingsReducer() {
        const { pending, fulfilled, rejected } = extraActions.fetchAllListings;
        return {
            [pending]: (state) => {
                state.loading = true;
                state.listings = [];
            },
            [fulfilled]: (state, action) => {
                state.loading = false;
                state.listings = action.payload?.properties;
                state.complexDropdown = action.payload?.complexDropdown;
                state.floorPlanDropdown = action.payload?.floorPlanDropdown;
                state.totalData = action?.payload?.totalProperties;
                state.count = {
                    totalProperties: action?.payload.totalProperties,
                    totalPublishedProperties: action?.payload?.totalPublishedProperties,
                    totalUnpublishedProperties: action?.payload?.totalUnpublishedProperties
                };
            },
            [rejected]: (state, action) => {
                state.listings = [];
                state.loading = false;
                state.error = action.error.message;
            }
        };
    }

    function fetchSharedListingsReducer() {
        const { pending, fulfilled, rejected } = extraActions.fetchSharedListings;
        return {
            [pending]: (state) => {
                state.loading = true;
                state.listings = [];
            },
            [fulfilled]: (state, action) => {
                state.loading = false;
                state.listings = action.payload;
            },
            [rejected]: (state, action) => {
                state.listings = [];
                state.loading = false;
                state.error = action.error.message;
            }
        };
    }

    function fetchListingDetails() {
        const { pending, fulfilled, rejected } = extraActions.fetchListingDetails;
        return {
            [pending]: (state) => {
                state.loading = true;
                state.singleListing = {};
            },
            [fulfilled]: (state, action) => {
                state.singleListing = action.payload;
                state.loading = false;
            },
            [rejected]: (state, action) => {
                state.singleListing = {};
                state.loading = false;
                state.error = action.error.message;
            }
        };
    }

    function editListingsReducer() {
        const { pending, fulfilled, rejected } = extraActions.editListings;
        return {
            [pending]: (state) => {
                state.loading = true;
            },
            [fulfilled]: (state, action) => {
                state.listings = state.listings.map((ele) => (ele?.id === action.payload?.data?.id ? action?.payload?.data : ele));
                state.loading = false;
            },
            [rejected]: (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            }
        };
    }

    function deleteListingsReducer() {
        const { pending, fulfilled, rejected } = extraActions.deleteListings;
        return {
            [pending]: (state) => {
                state.loading = true;
            },
            [fulfilled]: (state, action) => {
                state.listings = state.listings?.filter((property) => !action.payload?.data?.deletedProperties?.includes(property?.id));
                state.loading = false;
            },
            [rejected]: (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            }
        };
    }
}
